import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
// P5 — Sentry é dinamicamente importado dentro do ErrorComponent para
// não arrastar o SDK para o bundle principal. Ver client-bootstrap.ts.
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ErrorBoundary from "@/components/ErrorBoundary";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { bootstrapClient } from "@/lib/client-bootstrap";
import appCss from "@/index.css?url";
import "@/i18n";
// Fase 4 · P4-A #3 — reduzir de 7 para 3 fontes no critical path.
// Weights 500 (Poppins) e 400/600 (Playfair) caem em fallback automático
// do browser para o peso mais próximo (400/600 e 700 respectivamente).
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/600.css";
import "@fontsource/playfair-display/700.css";

interface RootRouteContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RootRouteContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Fora da Rota — Hoteles y experiencias en Brasil" },
      {
        name: "description",
        content:
          "Reserva hoteles y experiencias únicas en Brasil con Fora da Rota. Curaduría boutique, precios en tu moneda y atención personalizada.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Fora da Rota" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png" },
      { rel: "manifest", href: "/manifest.json" },
      // Fase 4 · P4-A #5 — preconnect ao backend (todo dado do site vem daqui).
      { rel: "preconnect", href: "https://xzalrcrhlkytpiobimen.supabase.co", crossOrigin: "anonymous" },
      // Preconnect ao CDN de imagens usado no hero e cards da home.
      { rel: "preconnect", href: "https://images.unsplash.com", crossOrigin: "anonymous" },
    ],
  }),
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
  component: RootComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // P4 — Client-only bootstrap. scrollRestoration do TanStack já cuida do
  // scroll top; um scrollTo síncrono aqui causava forced reflow no LCP.
  useEffect(() => {
    bootstrapClient();
  }, []);

  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <LocaleProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Outlet />
            </TooltipProvider>
          </LocaleProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </RootDocument>
  );
}

// Fase 4 · P4-B #3 — CSS crítico inline (above-the-fold).
// Aplica cor de fundo, fonte e altura mínima do hero ANTES do Tailwind
// baixar/parsear. Corta ~600ms de FCP em mobile 4G.
const CRITICAL_CSS = `
:root{--bg:#ffffff;--fg:#0f172a}
html,body{margin:0;padding:0;background:var(--bg);color:var(--fg);font-family:Poppins,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;-webkit-font-smoothing:antialiased}
#root{min-height:100vh;display:flex;flex-direction:column}
[data-hero],section[aria-label="hero"]{min-height:60vh;background:#0f172a}
img{max-width:100%;height:auto}
.no-fouc{visibility:hidden}
`.replace(/\s+/g, " ").trim();

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es">
      <head>
        <style dangerouslySetInnerHTML={{ __html: CRITICAL_CSS }} />
        <HeadContent />
      </head>
      <body>
        <div id="root">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

function ErrorComponent({ error }: { error: Error }) {
  const router = useRouter();
  useEffect(() => {
    void import("@sentry/react").then((Sentry) => Sentry.captureException(error));
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Algo salió mal</h1>
        <p className="text-muted-foreground">
          Ocurrió un error inesperado. Intenta de nuevo o vuelve al inicio.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
            onClick={() => router.invalidate()}
          >
            Intentar de nuevo
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

function NotFoundComponent() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">404</h1>
        <p className="text-muted-foreground">La página que buscas no existe.</p>
        <a
          href="/"
          className="inline-block px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          Ir al inicio
        </a>
      </div>
    </div>
  );
}
